import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import Tooltip from '@mui/joy/Tooltip';
import Chip from '@mui/joy/Chip';
import Typography from '@mui/joy/Typography';
import getIcon from 'utils/getIcon';
import pluralize from 'utils/pluralize'

import useClock from 'hooks/useClock';
import useUserIdentity from 'hooks/useUserIdentity';


type Props = {
    associated_type: string;
    associated_uuid: string;
    size?: 'sm' | 'lg' | 'natural';
};
const ActiveCollaboratorsChip = observer(({ associated_type, associated_uuid, size='lg' }: Props) => {
    const CollaboratorsIcon = getIcon('Group');

    const [fetched, setFetched] = useState(false);
    const [lastCount, setLastCount] = useState(0);
    const [highlighted, setHighlighted] = useState(false);
    // const [activeClocks, setActiveClocks] = useState([] as Clock[]);
    const { currentIdentity } = useUserIdentity();
    const {
        fetchClocksByAssociation, getClocksByAssociation
    } = useClock();

    const clocks = getClocksByAssociation(associated_type, associated_uuid);
    const activeClocks = clocks.filter((c) => c.time_end === null);

    useEffect(() => {
        if (activeClocks.length !== lastCount) {
            setLastCount(activeClocks.length);
            setHighlighted(true);
            setTimeout(() => {
                setHighlighted(false);
            }, 1_500);
        }
    }, [activeClocks.length, lastCount]);


    useEffect(() => {
        if(fetched) return;
        if(!currentIdentity?.uuid) return;

        fetchClocksByAssociation(
            associated_type,
            associated_uuid,
            currentIdentity?.uuid
        ).then(() => {
            setFetched(true);
        });

    }, [associated_type, associated_uuid, fetched, currentIdentity?.uuid]);


    if(!fetched) { return null; }
    if(!activeClocks.length) { return null; }
    const intro = (activeClocks.length == 1 ? "One person is" : `${activeClocks.length} people are`)
    return (
        <Tooltip arrow
            title={`${intro} clocked in and working on this`}>
            <Chip
                sx={{ cursor: "default" }}
                startDecorator={<CollaboratorsIcon
                                    className={ highlighted ? "icon-ring" : "icon-stable"}
                                    color="success"
                />}
                variant="soft"
                color="success">
                { size === 'sm' &&
                    activeClocks.length }
                { size === 'lg' &&
                    pluralize(activeClocks.length, 'Active Collaborator')}
                {size === 'natural' && (
                    <>
                        <Typography
                            display={{ xs: 'inline', sm: 'none' }}>
                            {activeClocks.length}
                        </Typography>
                        <Typography
                            level="body-sm"
                            color="success"
                            display={{ xs: 'none', sm: 'inline' }}>
                            {pluralize(activeClocks.length, 'Active Collaborator')}
                        </Typography>
                    </>
                )}
            </Chip>
        </Tooltip>
    );
});

export default ActiveCollaboratorsChip;
